import { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import ClassDetailsPanel from "./ClassDetailsPanel";
import { fmtShort, fmtTime, weekdayOf, programColor, isFull, isBookingClosed } from "../../lib/calendarUtils";

const fmtMoney = (n) => (typeof n === "number" ? `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}` : "");

export default function MobileAgenda({ dates, occurrences, todayStr, onBook }) {
  const firstWithClass = dates.find((d) => occurrences.some((o) => o.date === d));
  const [day, setDay] = useState(firstWithClass || todayStr || dates[0]);
  const [open, setOpen] = useState(null); // occurrence in drawer

  const activeDay = dates.includes(day) ? day : firstWithClass || dates[0];
  const dayOccs = useMemo(() => occurrences.filter((o) => o.date === activeDay), [occurrences, activeDay]);

  return (
    <div data-testid="mobile-agenda">
      {/* Day selector chips */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1">
        {dates.map((d) => {
          const count = occurrences.filter((o) => o.date === d).length;
          const sel = d === activeDay;
          return (
            <button
              key={d}
              type="button"
              onClick={() => setDay(d)}
              data-testid={`mobile-day-${weekdayOf(d).toLowerCase()}`}
              className={`flex flex-col items-center rounded-2xl px-3.5 py-2 min-w-[62px] border transition-colors ${
                sel ? "bg-[#123B6D] border-[#123B6D] text-white" : "bg-white border-slate-200 text-slate-700"
              }`}
            >
              <span className="text-[10px] font-bold uppercase tracking-wide opacity-80">{weekdayOf(d)}</span>
              <span className="text-sm font-extrabold font-display">{fmtShort(d).split(" ")[1]}</span>
              <span className={`mt-0.5 h-1.5 w-1.5 rounded-full ${count ? (sel ? "bg-[#FF8C42]" : "bg-[#00A6B2]") : "bg-transparent"}`} />
            </button>
          );
        })}
      </div>

      {/* Agenda list */}
      <div className="mt-3 grid gap-2.5">
        {dayOccs.length === 0 ? (
          <p className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="mobile-agenda-empty">
            No classes on {weekdayOf(activeDay)} {fmtShort(activeDay)}.
          </p>
        ) : (
          dayOccs.map((o) => {
            const s = o.schedule;
            const c = programColor(s);
            const full = isFull(s);
            const closed = isBookingClosed(s);
            const p = s.pricing || {};
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => setOpen(o)}
                data-testid={`mobile-slot-${o.key}`}
                className={`text-left rounded-2xl border-l-4 border border-slate-200 ${c.border} bg-white p-4 flex items-center gap-3 active:bg-slate-50`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-display font-extrabold text-sm tracking-tight truncate" style={{ color: "#123B6D" }}>{s.title}</p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
                    {s.age_range ? ` · Ages ${s.age_range}` : ""}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {s.billing_type === "subscription" ? `${fmtMoney(p.monthly_amount)}/mo` : fmtMoney(p.total_due_today)}
                    {" · "}
                    <span className={full || closed ? "font-bold text-slate-500" : "font-semibold text-emerald-600"}>
                      {closed ? "Booking closed" : full ? "Full" : `${s.seats_available} seats`}
                    </span>
                  </p>
                </div>
                <span className="shrink-0 text-xs font-bold" style={{ color: c.hex }}>Details ›</span>
              </button>
            );
          })
        )}
      </div>

      {/* Bottom drawer with details */}
      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent side="bottom" className="rounded-t-[1.5rem] max-h-[88vh] overflow-y-auto p-4 pt-3" data-testid="mobile-details-drawer">
          <SheetHeader className="sr-only">
            <SheetTitle>{open?.schedule?.title || "Class details"}</SheetTitle>
          </SheetHeader>
          <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-slate-300" />
          <ClassDetailsPanel
            occ={open}
            compact
            onBook={(s) => {
              setOpen(null);
              onBook(s);
            }}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
